import React from 'react';
import {
  ConsumerOrderDishSelectionItemFragment,
  DishDescriptionFoodItemFragment,
} from 'src/gqlReactTypings.generated.d';
import { useDietaryTags } from 'src/pages/consumer/account/refer/ReferOrder/LineItem/hooks';
import { LinkButton } from 'src/shared/design-system/Atoms';
import { DietaryTagIconSet } from 'src/shared/design-system/DietaryTagIconSet';
import { DynamicallyTruncatedParagraph, Paragraph, Truncate } from 'src/shared/design-system/Text';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import styled from 'styled-components';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { Colors, Font, Spacing } from 'web-common/src/shared/styles';
import { DishPrice } from './DishPrice';
import { DishTitle } from './DishTitle';
import { UnavailableOverlay } from './UnavailableOverlay';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-start;
  min-height: 0;
  padding: ${Spacing.SINGLE};
  padding-top: ${Spacing.SINGLE_HALF};
  // Needed for soldout absolute positioned overlays
  position: relative;
`;

const AdminEditBtn = styled(LinkButton)`
  font-size: 14px;
  color: ${Colors.GRAY};
  margin-left: 5px;
  height: 22px;
`;

const DishInfoContainer = styled.div<{ marginBottom?: string; alignStart?: boolean }>`
  margin: ${Spacing.NONE};
  margin-bottom: ${(props) => props.marginBottom ?? Spacing.THREE_QUARTERS};
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: ${ifProp('alignStart', 'flex-start', 'space-between')};
`;

const DishTitleContainer = styled.div`
  margin-bottom: ${Spacing.QUARTER};
`;

interface IProps {
  isBigImage?: boolean;
  showSoldOutOverlay: boolean;
  showAdminEditButton: boolean;
  onSoldOutOverlayClick: () => void;
  onAdminEditClick: () => void;
  foodItem: ConsumerOrderDishSelectionItemFragment & DishDescriptionFoodItemFragment;
}

export const DishDescription: React.FC<IProps> = ({
  foodItem,
  onSoldOutOverlayClick,
  onAdminEditClick,
  showSoldOutOverlay,
  showAdminEditButton,
}) => {
  const { showDietaryTags, dietaryTags } = useDietaryTags(foodItem);
  const { isSmallScreen } = useScreenContext();

  return (
    <Container>
      {showSoldOutOverlay && <UnavailableOverlay onClick={onSoldOutOverlayClick} />}
      <DishTitleContainer>
        <DishInfoContainer marginBottom={Spacing.NONE}>
          <DishTitle>{foodItem.name ?? ''}</DishTitle>
          {showAdminEditButton && <AdminEditBtn onClick={onAdminEditClick}>Admin edit</AdminEditBtn>}
        </DishInfoContainer>
        <Paragraph size={Font.Size.XXS} style={{ marginBottom: 0 }}>
          {foodItem.portion}
        </Paragraph>
      </DishTitleContainer>
      <DishInfoContainer alignStart={true} marginBottom={Spacing.HALF}>
        <DishPrice price={foodItem.price} />
        {showDietaryTags && <DietaryTagIconSet spiceLevel={foodItem.spiceLevel} tags={dietaryTags} />}
      </DishInfoContainer>
      {foodItem.description &&
        (isSmallScreen ? (
          <Truncate lines={2}>
            <Paragraph size={Font.Size.XS} weight={Font.Weight.NORMAL}>
              {foodItem.description}
            </Paragraph>
          </Truncate>
        ) : (
          <DynamicallyTruncatedParagraph
            lineHeight={24}
            initialNumberOfLines={2}
            size={Font.Size.XS}
            weight={Font.Weight.NORMAL}
            style={{ flexGrow: 1, margin: 0 }}>
            {foodItem.description}
          </DynamicallyTruncatedParagraph>
        ))}
    </Container>
  );
};
